import React, { useEffect, useState } from "react";
import { Storage } from "aws-amplify";
import { getImageType } from "../../../../core/utils/general/strings";
import { multiImageZipKey } from "../../../../core/utils/dataloading/multiImageZipKey";
import {
  Row,
  Col,
  Card,
  Tooltip,
  Popconfirm,
  Typography,
  Upload,
  Form,
  Modal,
  Input,
  message,
  List,
  Tag,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import BulkUploadResultsModal from "./creatives/BulkUploadResultModal";
import "./../../Creatives/library/creativestyle.css";
import Button from "../../../shared/globalStyling/Button";
import SearchBar from "../../Creatives/library/SearchBar";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";
import AddVideosModal from "../../Creatives/components/AddVideosModal";
import {
  getVideoDuration,
  getVideoDimensions,
  getVideoMetadata,
  getFileNameWithoutExtension,
  uploadToS3Storage,
  transformFileName
} from "../../Creatives/utils/mediaUtils";
const { Title } = Typography;
const { Dragger } = Upload;

const VideoTab = ({
  videoCreativeIds,
  onCreateAdgroup,
  onDeleteMedias,
  refetchVideoMedias,
  searchText,
  handleSearch,
  setVideoCreativeIds,
  videoCreativeKeys,
  setVideoCreativeKeys,
  bulkCreateOrgMedia,
  clearAllSelections,
}) => {
  const allowedVideoTypes = ["video/mp4"];
  const {
    createOrgMedia,
    orgVideoMedias,
    updateAdGroupCreatives,
    adGroupCreatives,
  } = useLibraryCreativesContext() || {};
  const [form] = Form.useForm();
  const [formAdGroup] = Form.useForm();
  const [uploadModal, setUploadModal] = useState(false);
  const [adGroupModal, setAdGroupModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [assignAdGroupModalVisible, setAssignAdGroupModalVisible] = useState(
    false
  );
  const [selectedAdGroupIds, setSelectedAdGroupIds] = useState([]);
  const [selectedAdUnits, setSelectedAdUnits] = useState([]);

  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadResult, setUploadResults] = useState([]);
  const [
    showUploadResultsTableModal,
    setShowUploadResultsTableModal,
  ] = useState(false);

  // Unified upload state
  const [uploadState, setUploadState] = useState({
    mode: "idle", // 'idle', 'single', 'bulk'
    files: [],
    validationResults: [],
    uploadProgress: {},
    errors: [],
    currentFile: null,
    videoUrl: null,
    videoDimensions: null,
    key: null,
  });

  /* UNUSED: Legacy state for backwards compatibility - replaced by uploadState
  const [fileList, setFileList] = useState([]);
  */

  // Execute query to refetch videos after upload/delete/createadgroup
  const refetchVideos = async () => {
    refetchVideoMedias({
      variables: {
        type: 'VIDEO',
      },
    });
  };

  // Clear fields after single video upload or cancel
  const clearFields = async () => {
    form.resetFields();
    setUploadState({
      mode: "idle",
      files: [],
      validationResults: [],
      uploadProgress: {},
      errors: [],
      currentFile: null,
      videoUrl: null,
      videoDimensions: null,
      key: null,
    });
  };

  // Unified file validation function
  const validateVideoFile = async file => {
    const errors = [];
    const warnings = [];

    // Check file type
    if (!allowedVideoTypes.includes(file.type)) {
      errors.push("Invalid file type. Only MP4 is allowed.");
    }

    try {
      // Get comprehensive video metadata
      const metadata = await getVideoMetadata(file);
      
      // Check duration
      if (metadata.duration < 15 || metadata.duration > 30) {
        errors.push("Video duration must be between 15 and 30 seconds.");
      }

      // Check for video property issues that might cause upload problems
      if (metadata.hasIssues) {
        metadata.issues.forEach(issue => {
          if (issue.includes('High bitrate')) {
            warnings.push(`🚨 ${issue} - This high bitrate may cause S3 multipart upload failures. Consider re-encoding the video with lower bitrate.`);
          } else if (issue.includes('High resolution')) {
            warnings.push(`⚠️ ${issue} - Consider reducing resolution to improve upload reliability`);
          } else if (issue.includes('aspect ratio')) {
            warnings.push(`📐 ${issue} - Unusual format detected, may cause encoding issues`);
          } else {
            warnings.push(`⚠️ ${issue}`);
          }
        });
      }

      // Special case for your 19.6MB problematic file size range
      if (file.size > 15 * 1024 * 1024 && file.size < 25 * 1024 * 1024) {
        warnings.push(`📦 File size ${(file.size / 1024 / 1024).toFixed(1)}MB is in problematic range (15-25MB) - upload will use simple configuration to avoid multipart issues`);
      }


      return {
        valid: errors.length === 0,
        errors,
        warnings,
        duration: metadata.duration,
        dimensions: { width: metadata.width, height: metadata.height },
        metadata,
        file,
      };
    } catch (error) {
      console.error('Video analysis failed:', error);
      errors.push("Failed to analyze video file. File may be corrupted or unsupported.");
      return {
        valid: false,
        errors,
        warnings: [],
        duration: 0,
        dimensions: { width: 0, height: 0 },
        metadata: null,
        file,
      };
    }
  };

  // Smart file selection handler
  const handleUnifiedFileSelection = async info => {
    const files = info.fileList.map(
      fileInfo => fileInfo.originFileObj || fileInfo
    );

    if (files.length === 0) {
      setUploadState(prev => ({ ...prev, mode: "idle", files: [] }));
      return;
    }

    // Validate all files
    const validationResults = await Promise.all(
      files.map(file => validateVideoFile(file))
    );

    const mode = files.length === 1 ? "single" : "bulk";

    setUploadState({
      mode,
      files,
      validationResults,
      uploadProgress: {},
      errors: [],
      currentFile: files.length === 1 ? files[0] : null,
      videoUrl: null,
      videoDimensions: null,
      key: null,
    });

    // Show validation errors
    const invalidFiles = validationResults.filter(result => !result.valid);
    if (invalidFiles.length > 0) {
      const errorMessage = `${invalidFiles.length} of ${files.length} files need attention`;
      message.error(errorMessage);
    }

    // For single file mode, set up preview if valid
    if (mode === "single" && validationResults[0].valid) {
      await setupSingleFilePreview(files[0], validationResults[0]);
    }
  };

  // Setup single file preview
  const setupSingleFilePreview = async (file, validationResult) => {
    try {
      message.loading("Uploading video file...", 0);
      const response = await uploadToS3Storage(file, "video/mp4");
      message.destroy();

      setUploadState(prev => ({
        ...prev,
        videoUrl: response.url,
        videoDimensions: validationResult.dimensions,
        key: response.key,
      }));

      /* UNUSED: Legacy state is now handled in unified uploadState */

      // Auto-fill form name
      if (!form.getFieldValue("name")) {
        const fileName = getFileNameWithoutExtension(file.name);
        form.setFieldsValue({ name: fileName });
      }
      
      message.success("Video uploaded successfully!");
    } catch (error) {
      message.destroy();
      console.error("Video upload error:", error);
      
      // Provide more specific error messages
      let errorMessage = "Failed to upload video file.";
      if (error.message?.includes('multipart') || error.message?.includes('NoSuchUpload')) {
        errorMessage = "Upload configuration issue. The system will retry with a different approach.";
      } else if (error.message?.includes('network')) {
        errorMessage = "Network error during upload. Please check your connection and try again.";
      } else if (error.message?.includes('timeout')) {
        errorMessage = "Upload timed out. Please try again with a smaller file.";
      } else if (error.message?.includes('retry')) {
        errorMessage = "Upload failed even after retry. This file may have compatibility issues. Please try a different file.";
      }
      
      message.error(errorMessage);
      
      // Reset upload state on error
      setUploadState(prev => ({
        ...prev,
        videoUrl: null,
        videoDimensions: null,
        key: null,
      }));
    }
  };

  // Unified upload modal content
  const renderUnifiedUploadContent = () => {
    const { mode, files, validationResults } = uploadState;

    if (mode === "single") {
      return (
        <div>
          <Title level={5}>Upload Video (or select multiple files)</Title>
          <Dragger
            multiple={true}
            accept="video/mp4"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleUnifiedFileSelection}
            style={{ marginBottom: 16 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Drop video file(s) here or click to browse
            </p>
            <p className="ant-upload-hint">
              Supports MP4 files (15-30 seconds, max 5MB)
              <br />
              💡 Hold Ctrl/Cmd while clicking to select multiple files at once
              <br />
              ⚠️ Files larger than 5MB will be rejected - please compress your video
            </p>
          </Dragger>

          {uploadState.videoUrl && (
            <div style={{ marginTop: 16 }}>
              <video controls width="100%" height="auto">
                <source src={uploadState.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                You can select more files above to switch to bulk upload mode
              </p>
            </div>
          )}

          {validationResults.length > 0 && (
            <div style={{ marginTop: 16 }}>
              {/* Show errors */}
              {!validationResults[0].valid && validationResults[0].errors.map((error, index) => (
                <Tag key={`error-${index}`} color="red" style={{ marginBottom: 4, display: 'block' }}>
                  {error}
                </Tag>
              ))}
              
              {/* Show warnings even for valid files */}
              {validationResults[0].warnings && validationResults[0].warnings.length > 0 && 
                validationResults[0].warnings.map((warning, index) => (
                  <Tag key={`warning-${index}`} color="orange" style={{ marginBottom: 4, display: 'block' }}>
                    {warning}
                  </Tag>
                ))
              }
            </div>
          )}
        </div>
      );
    }

    if (mode === "bulk") {
      return (
        <div>
          <Title level={5}>Bulk Upload Videos ({files.length} files)</Title>
          <Dragger
            multiple={true}
            accept="video/mp4"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleUnifiedFileSelection}
            style={{ marginBottom: 16 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Drop video files here or click to browse
            </p>
            <p className="ant-upload-hint">
              Supports multiple MP4 files (15-30 seconds each, max 5MB per file)
              <br />
              ⚠️ Files larger than 5MB will be rejected - please compress your videos
            </p>
          </Dragger>

          <List
            size="small"
            dataSource={files}
            renderItem={(file, index) => {
              const validation = validationResults[index];
              return (
                <List.Item
                  actions={[
                    <Button
                      key="remove"
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeFileFromBulk(index)}
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={file.name}
                    description={
                      <div>
                        <div>
                          Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                        {validation && (
                          <div>
                            {validation.valid ? (
                              <div>
                                <Tag color="green">Valid</Tag>
                                {/* Show warnings even for valid files */}
                                {validation.warnings && validation.warnings.length > 0 && 
                                  validation.warnings.map((warning, warnIndex) => (
                                    <Tag
                                      key={`warn-${warnIndex}`}
                                      color="orange"
                                      style={{ marginBottom: 2, fontSize: '11px' }}
                                    >
                                      {warning}
                                    </Tag>
                                  ))
                                }
                              </div>
                            ) : (
                              <div>
                                {validation.errors.map((error, errorIndex) => (
                                  <Tag
                                    key={errorIndex}
                                    color="red"
                                    style={{ marginBottom: 2 }}
                                  >
                                    {error}
                                  </Tag>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      );
    }

    // Default idle state
    return (
      <div>
        <Title level={5}>Upload Video(s)</Title>
        <Dragger
          multiple={true}
          accept="video/mp4"
          showUploadList={false}
          beforeUpload={() => false}
          onChange={handleUnifiedFileSelection}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Drop video file(s) here or click to browse
          </p>
          <p className="ant-upload-hint">
            Supports single or multiple MP4 files (15-30 seconds each, max 5MB per file)
            <br />
            💡 Select multiple files at once using Ctrl/Cmd + Click
            <br />
            ⚠️ Files larger than 5MB will be rejected - please compress your videos
          </p>
        </Dragger>
      </div>
    );
  };

  // Remove file from bulk upload
  const removeFileFromBulk = index => {
    const newFiles = uploadState.files.filter((_, i) => i !== index);
    const newValidationResults = uploadState.validationResults.filter(
      (_, i) => i !== index
    );

    const newMode =
      newFiles.length === 0
        ? "idle"
        : newFiles.length === 1
        ? "single"
        : "bulk";

    setUploadState(prev => ({
      ...prev,
      mode: newMode,
      files: newFiles,
      validationResults: newValidationResults,
    }));

    // If switching to single mode, setup preview
    if (
      newMode === "single" &&
      newValidationResults[0] &&
      newValidationResults[0].valid
    ) {
      setupSingleFilePreview(newFiles[0], newValidationResults[0]);
    }
  };

  // Modal title based on upload mode
  const getModalTitle = () => {
    const { mode, files } = uploadState;
    switch (mode) {
      case "single":
        return "Upload Video";
      case "bulk":
        return `Bulk Upload Videos (${files.length} files)`;
      default:
        return "Upload Video(s)";
    }
  };

  // Modal footer based on upload mode
  const getModalFooter = () => {
    const { mode, validationResults } = uploadState;

    if (mode === "single") {
      const isValid =
        validationResults.length > 0 && validationResults[0].valid;
      return [
        <Button
          key="cancel"
          onClick={() => {
            clearFields();
            setUploadModal(false);
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!isValid || loading}
          onClick={() => form.submit()}
        >
          Save Video
        </Button>,
      ];
    }

    if (mode === "bulk") {
      const validFiles = validationResults.filter(result => result.valid);
      const hasValidFiles = validFiles.length > 0;

      return [
        <Button
          key="cancel"
          onClick={() => {
            clearFields();
            setUploadModal(false);
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!hasValidFiles || loading}
          onClick={handleBulkUpload}
        >
          Upload {validFiles.length} Video{validFiles.length !== 1 ? "s" : ""}
        </Button>,
      ];
    }

    return [
      <Button
        key="cancel"
        onClick={() => {
          clearFields();
          setUploadModal(false);
        }}
      >
        Cancel
      </Button>,
    ];
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    const { validationResults } = uploadState;
    const validFiles = validationResults.filter(result => result.valid);

    if (validFiles.length === 0) {
      message.error("No valid files to upload");
      return;
    }

    setUploadLoading(true);
    setUploadComplete(false);
    setUploadResults([]);
    setUploadModal(false);
    setShowUploadResultsTableModal(true);

    const results = [];

    try {
      for (const validFile of validFiles) {
        const { file, duration, dimensions } = validFile;

        try {
          // Upload to S3 with individual error handling
          const response = await uploadToS3Storage(file, "video/mp4");

          // Prepare result object for UI
          const result = {
            name: file.name.replace(/\.[^/.]+$/, ""),
            dimensions: `${dimensions.width}x${dimensions.height}`,
            fileSize: file.size,
            duration,
            status: true,
          };

          // Update progress
          setUploadResults(prev => [...prev, result]);

          // Prepare for bulk create
          results.push({
            type: "VIDEO",
            key: response.key,
            url: response.url,
            width: dimensions.width,
            height: dimensions.height,
            name: file.name.replace(/\.[^/.]+$/, ""),
            size: file.size,
            fileType: "MP4",
          });
        } catch (uploadError) {
          console.error(`Failed to upload ${file.name}:`, uploadError);
          
          // Add failed result to UI
          const failedResult = {
            name: file.name.replace(/\.[^/.]+$/, ""),
            dimensions: `${dimensions.width}x${dimensions.height}`,
            fileSize: file.size,
            duration,
            status: false,
            error: uploadError.message?.includes('multipart') 
              ? "Large file upload failed" 
              : "Upload failed",
          };
          
          setUploadResults(prev => [...prev, failedResult]);
        }
      }

      // Bulk create all videos
      await bulkCreateOrgMedia(results, [], true);
      await refetchVideos();

      setUploadComplete(true);
      message.success(`Successfully uploaded ${results.length} video(s)`);

      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowUploadResultsTableModal(false);
      }, 2000);
    } catch (error) {
      message.error("Bulk upload failed");
    } finally {
      setUploadLoading(false);
      clearFields();
    }
  };

  // Function to handle form submission for uploading a single video
  const handleSubmit = async () => {
    setLoading(true);

    try {
      // Validate using unified upload state
      const {
        validationResults,
        key: uploadKey,
        videoUrl: uploadVideoUrl,
        videoDimensions: uploadDimensions,
      } = uploadState;

      if (!uploadKey || !uploadVideoUrl) {
        message.error("Please select a file to upload");
        setLoading(false);
        return;
      }

      if (validationResults.length === 0 || !validationResults[0].valid) {
        message.error("Please upload a valid video file");
        setLoading(false);
        return;
      }

      const { file } = validationResults[0];
      const fileName = form.getFieldValue("name");

      const CapitalizeCreativeName = fileName.charAt(0).toUpperCase() + fileName.slice(1);

      await createOrgMedia({
        variables: {
          mediatype: "VIDEO",
          key: uploadKey,
          url: uploadVideoUrl,
          width: uploadDimensions.width,
          height: uploadDimensions.height,
          name: CapitalizeCreativeName,
          size: file.size,
          fileType: getImageType(file.type),
        },
      });

      message.success('Video Creative saved successfully!');

      await refetchVideos();

      clearFields();
      setUploadModal(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      message.error(`File upload failed: ${error.message}}`);
    }
  };

  const handleAssignAdUnits = async () => {
    setLoading(true);
    try {
      // Use the proper assignment approach - connect media to existing ad groups
      await Promise.all(
        selectedAdGroupIds.map(adGroupId =>
          updateAdGroupCreatives({
            variables: {
              id: adGroupId,
              data: {
                medias: {
                  connect: selectedAdUnits.map(unit => ({ id: unit.id })),
                },
              },
            },
          })
        )
      );
      message.success("Videos assigned to ad group(s)!");
      setAssignAdGroupModalVisible(false);
      setSelectedAdGroupIds([]);
      setSelectedAdUnits([]);

      // Clear local selections immediately
      setVideoCreativeIds([]);

      // Clear all selections through parent state management
      if (clearAllSelections) {
        clearAllSelections();
      }
      
      await refetchVideos();
    } catch (error) {
      message.error("Failed to assign videos to ad group(s)");
    }
    setLoading(false);
  };
  // Function to handle creating an ad group with selected video creatives
  const handleCreateAdGroup = async values => {
    setLoading(true);
    formAdGroup.resetFields();

    const remakeIds = videoCreativeIds.map(idVal => ({ id: idVal }));
    const adGroupName = values.name;

    try {
      // Use the parent's onCreateAdgroup function instead of calling GraphQL directly
      await onCreateAdgroup(adGroupName, remakeIds);

      setLoading(false);
      setAssignAdGroupModalVisible(false); // Close the assignment modal too
      setAdGroupModal(false); // Close the create ad group modal after creation
      formAdGroup.resetFields();

      // Note: selection clearing is handled by parent LibraryCreativesViewImproved
      setSelectedAdUnits([]);
      setSelectedAdGroupIds([]);
    } catch (error) {
      setLoading(false);
      message.error("Failed to create Video Ad Group");
    }
  };

  // Function to handle deleting video creatives
  const handleDelete = async videoCreativeIds => {
    setLoading(true);
    await onDeleteMedias(videoCreativeIds);
    await refetchVideos();
    setLoading(false);
    if (typeof setVideoCreativeIds === "function") {
      setVideoCreativeIds([]);
    }
  };
  
  /* UNUSED: Legacy customRequest function removed - no longer needed
  // Utility functions have been moved to mediaUtils.js to avoid duplication
  */

  /* UNUSED: Legacy fileList useEffect - replaced by uploadState management
  useEffect(() => {
    // Clear the preview when the file has been removed
    if (!fileList.length) {
      // Video preview is now handled in unified uploadState
      // No need to clear old state variables
    }
  }, [fileList]);
  */
  return (
    <>
      <BulkUploadResultsModal
        visible={showUploadResultsTableModal}
        uploadResults={uploadResult}
        uploadInProgress={uploadLoading}
        uploadComplete={uploadComplete}
        onCancel={() => {
          setShowUploadResultsTableModal(false);
        }}
      />
      <Modal
        title={getModalTitle()}
        open={uploadModal}
        closable={!loading}
        maskClosable={!loading}
        keyboard={!loading}
        centered
        width={uploadState.mode === "bulk" ? 800 : 600}
        footer={getModalFooter()}
        onCancel={() => {
          clearFields();
          setUploadModal(false);
        }}
      >
        {uploadState.mode === "single" ? (
          <Form form={form} onFinish={handleSubmit}>
            <Row justify="center" align="middle">
              <Col span={24}>
                {renderUnifiedUploadContent()}

                {uploadState.videoUrl && (
                  <Form.Item
                    label="Video Name"
                    name="name"
                    rules={[
                      { required: true, message: "Please enter video name" },
                    ]}
                    style={{ marginTop: 16 }}
                  >
                    <Input placeholder="Enter video name" />
                  </Form.Item>
                )}
              </Col>
            </Row>
          </Form>
        ) : (
          <div>{renderUnifiedUploadContent()}</div>
        )}
      </Modal>
      <AddVideosModal
        setAdGroupModalVisible={setAssignAdGroupModalVisible}
        adGroupModalVisible={assignAdGroupModalVisible}
        setAdGroupModal={setAdGroupModal}
        ids={videoCreativeIds}
        orgMedias={orgVideoMedias ? orgVideoMedias.medias : []}
        selectedAdUnits={selectedAdUnits}
        adGroupCreatives={adGroupCreatives}
        selectedAdGroupIds={selectedAdGroupIds}
        setSelectedAdGroupIds={setSelectedAdGroupIds}
        handleAssignAdUnits={handleAssignAdUnits}
        assigning={loading}
      />
      <Modal
        title={`Create Video Ad Group - ${videoCreativeIds.length} Creative(s) Selected`}
        open={adGroupModal}
        centered
        footer={null}
        onCancel={() => {
          setAdGroupModal(false);
          formAdGroup.resetFields();
        }}
      >
        <Form layout="vertical" onFinish={handleCreateAdGroup} form={formAdGroup}>
          <Row type="flex" justify="space-around">
            <Col span={24}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input placeholder="Name of Video Ad Group" />
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" justify="space-around" align="middle">
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button htmlType="submit" type="primary">
                  Create
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Row>
        <Col xs={24} xl={24}>
          <Card bodyStyle={{ padding: 10 }}>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col>
                <Title level={4}>Video Ad Units</Title>
              </Col>
              <Col>
                <SearchBar searchText={searchText} onSearch={handleSearch} />
              </Col>
            </Row>
            <Row align="middle" className="ant-row-justify-space-between">
              <Col xs={24} sm={16} md={16} lg={14}>
                <Row>
                  <Popconfirm
                    placement="topLeft"
                    title={`Are you sure you want to delete the selected creatives? This action cannot be undone.`}
                    onConfirm={() => {
                      handleDelete(videoCreativeIds);
                    }}
                    okText="Delete"
                    cancelText="Cancel"
                  >
                    <Tooltip
                      placement="bottom"
                      title={`Delete selected creatives`}
                    >
                      <Button
                        type="default"
                        disabled={!videoCreativeIds || videoCreativeIds.length === 0}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                  <Button
                    type="default"
                    disabled={
                      !videoCreativeIds || videoCreativeIds.length === 0
                    }
                    onClick={() =>
                      multiImageZipKey(
                        "Video Creatives (" +
                          videoCreativeIds.length +
                          " videos)",
                        videoCreativeKeys
                      )
                    }
                  >
                    Download Ad Unit
                  </Button>
                  <Button
                    type="default"
                    disabled={
                      !videoCreativeIds || videoCreativeIds.length === 0
                    }
                    onClick={() => {
                      setSelectedAdUnits(
                        (orgVideoMedias
                          ? orgVideoMedias.medias
                          : []
                        ).filter(item => videoCreativeIds.includes(item.id))
                      );
                      setAssignAdGroupModalVisible(true);
                    }}
                  >
                    Assign to Ad Groups
                  </Button>
                </Row>
              </Col>
              <Col xs={24} sm={8} md={8} lg={10} style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModal(true)}
                  disabled={false}
                >
                  Upload Video(s)
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default VideoTab;
